import { ArrowBack, Close } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React from "react";
import { useSelector } from "react-redux";
import { RightPanel } from "../../../components/RightPanel";
import { ShareViaWhatsAppButton } from "../../../components/ShareViaWhatsApp";
import { useDispatchWrapper } from "../../../hooks";
import { useBillingCompanyContext } from "../../../providers";
import { IStoreState } from "../../../redux/initialStoreState";
import { ILead, IMultipleEvent } from "../../../redux/leads/leads.types";
import { fetchLeadAsync } from "../../../redux/leads/leadsActions";
import { ILeadFoodMenu, IMasterFoodMenu } from "../../../redux/food/food.types";
import {
  clearLeadFoodMenuState,
  fetchLeadFoodMenusListAsync,
  fetchMasterFoodMenusListAsync,
  upsertLeadFoodMenu,
} from "../../../redux/food/foodActions";
import { LoadState } from "../../../constants/enums";
import { FoodMenuBuilder } from "../../Food/FoodMenuBuilder";
import {
  IMenuBuilderOutput,
} from "../../Food/shared/types/foodPayload.types";
import { extractLimits } from "./helpers/leadFoodMenuUtils";
import { buildLeadPayload } from "./helpers/buildLeadPayload";
import { LeadFoodMenuPreview } from "./LeadFoodMenuPreview";
import { EventSelectionStep } from "./components/EventSelectionStep";
import { MasterMenuSelectionStep } from "./components/MasterMenuSelectionStep";

type WizardStep = "event" | "menu" | "builder";

interface IViewLeadFoodMenuRightPanelProps {
  open: boolean;
  leadUuid: string;
  multipleEventsUuid: string;
  selectedLeadData: ILead;
  onClose: () => void;
}

export const ViewLeadFoodMenuRightPanel: React.FC<
  IViewLeadFoodMenuRightPanelProps
> = ({ open, onClose, leadUuid, selectedLeadData, multipleEventsUuid }) => {
  const dispatch = useDispatchWrapper();
  const companyOutlet = useBillingCompanyContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Redux state
  const requestedLeadData = useSelector(
    (state: IStoreState) => state.leads.lead.data,
  );
  const leadFoodMenus = useSelector(
    (state: IStoreState) => state.food.leadFoodMenu.data,
  );
  const leadFoodMenuLoading = useSelector(
    (state: IStoreState) => state.food.leadFoodMenu.loading,
  );
  const masterMenusList = useSelector(
    (state: IStoreState) => state.food.foodMenu.data,
  );
  const masterMenusLoading = useSelector(
    (state: IStoreState) => state.food.foodMenu.loading,
  );

  // Wizard state
  const [step, setStep] = React.useState<WizardStep>("event");
  const [activeLeadUuid, setActiveLeadUuid] = React.useState<string>(
    selectedLeadData.leads_uuid as string,
  );
  const [selectedMasterMenu, setSelectedMasterMenu] = React.useState<IMasterFoodMenu | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [openPreview, setOpenPreview] = React.useState(false);
  const [previewMenuData, setPreviewMenuData] = React.useState<any>(null);
  const [userNavigatedBack, setUserNavigatedBack] = React.useState(false);

  // Derived
  const existingMenu: ILeadFoodMenu | undefined = leadFoodMenus?.[0];
  const events: IMultipleEvent[] = React.useMemo(() => {
    const lead = requestedLeadData || selectedLeadData;
    return lead.multiple_events_list && lead.multiple_events_list.length > 0
      ? lead.multiple_events_list
      : [
          {
            leads_uuid: selectedLeadData.leads_uuid,
            event_type: selectedLeadData.event_name || "Default Event",
            event_uuid: selectedLeadData.event_uuid || "",
            event_time: "",
            event_time_uuid: "",
            preferred_venue: null,
            preferred_venue_uuid: "",
            booking_date: selectedLeadData.booking_date || null,
            status: "",
            actual_status: "",
            drive_status_uuid: "",
            number_of_rooms: null,
            number_of_guests: selectedLeadData.number_of_guests || null,
          } as IMultipleEvent,
        ];
  }, [requestedLeadData, selectedLeadData]);

  const currentEventName = React.useMemo(() => {
    const event = events.find((e) => e.leads_uuid === activeLeadUuid);
    return event?.event_type || selectedLeadData.event_name || "";
  }, [activeLeadUuid, events, selectedLeadData.event_name]);

  const title = `${selectedLeadData.name_of_guest}'s ${currentEventName} Menu`;

  // ─── Effects ──────────────────────────────────────────────────────────────────

  // Fetch lead data on open
  React.useEffect(() => {
    if (open) {
      setUserNavigatedBack(false);
      setActiveLeadUuid(selectedLeadData.leads_uuid as string);
      dispatch(
        fetchLeadAsync({
          multiple_events_uuid: multipleEventsUuid,
          company_outlet_uuid: companyOutlet.comapny.company_outlet_uuid as string,
          onCallBack: () => {},
        }),
      );
      // Fetch master menus for step 2
      dispatch(
        fetchMasterFoodMenusListAsync({
          company_outlet_uuid: companyOutlet.comapny.company_outlet_uuid as string,
          page: 1,
          rowsPerPage: 100,
          pageLimit: 100,
        }),
      );
      // Determine initial step - always show event selection if multiple events
      // or if no menu exists yet (even for single event)
      setStep("event");
    }
  }, [open]);

  // Fetch lead food menus when activeLeadUuid changes
  React.useEffect(() => {
    if (open && activeLeadUuid) {
      dispatch(
        fetchLeadFoodMenusListAsync(
          activeLeadUuid,
          companyOutlet.comapny.company_outlet_uuid as string,
          null,
        ),
      );
    }
  }, [open, activeLeadUuid]);

  // Auto-skip event step only if single event AND existing menu freshly loaded
  React.useEffect(() => {
    if (step === "event" && events.length <= 1 && existingMenu && leadFoodMenuLoading === LoadState.Loaded && !userNavigatedBack) {
      setStep("builder");
    }
  }, [events, step, existingMenu, leadFoodMenuLoading, userNavigatedBack]);

  // If existing menu found and we're on menu step, user can continue
  // If existing menu and panel re-opened, go directly to builder
  React.useEffect(() => {
    if (existingMenu && step === "menu") {
      // Stay on menu step — user can "Continue with existing" or pick different
    }
  }, [existingMenu, step]);

  // ─── Handlers ─────────────────────────────────────────────────────────────────

  const handleSelectEvent = (eventLeadUuid: string) => {
    setUserNavigatedBack(false);
    setActiveLeadUuid(eventLeadUuid);
    setSelectedMasterMenu(null);
    setStep("menu");
  };

  const handleSelectMasterMenu = (menu: IMasterFoodMenu) => {
    setSelectedMasterMenu(menu);
    setStep("builder");
  };

  const handleContinueExisting = () => {
    setStep("builder");
  };

  const handleBackToMenus = () => {
    setStep("menu");
  };

  const handleBackToEvents = () => {
    setUserNavigatedBack(true);
    setStep("event");
  };

  const handleSave = (output: IMenuBuilderOutput) => {
    const payload = buildLeadPayload(output, {
      addedLeadMenuUuid: existingMenu?.added_lead_menu_uuid ?? null,
      leadUuid: activeLeadUuid,
      title,
      companyOutletUuid: companyOutlet.comapny.company_outlet_uuid,
      masterMenuUuid: selectedMasterMenu?.master_menu_uuid ?? existingMenu?.master_menu_uuid ?? null,
      masterMenuName: selectedMasterMenu?.title ?? existingMenu?.master_menu_name ?? null,
      masterMenuPrice: selectedMasterMenu?.price != null
        ? String(selectedMasterMenu.price)
        : existingMenu?.master_menu_price ?? null,
    });

    setSaving(true);
    dispatch(
      upsertLeadFoodMenu(payload as ILeadFoodMenu, (success) => {
        setSaving(false);
        if (success) handleClose();
      }),
    );
  };

  const handleClose = () => {
    dispatch(clearLeadFoodMenuState());
    setStep("event");
    setActiveLeadUuid(selectedLeadData.leads_uuid as string);
    setSelectedMasterMenu(null);
    onClose();
  };

  // WhatsApp payload
  const whatsAppRequest = selectedLeadData
    ? {
        company_outlet_uuid: companyOutlet.comapny.company_outlet_uuid,
        env_key: "GENERIC_FOOD_MENU_WA",
        toNumber: selectedLeadData.contact_number_one,
        payload: {
          name_of_guest: selectedLeadData.name_of_guest,
          menu_message:
            "We are delighted to share our menu with you. Click the button below to explore our delicious offerings.",
          menu_url: companyOutlet.comapny.company_outlet_uuid,
        },
      }
    : null;

  // ─── Determine menu data for builder ──────────────────────────────────────────
  // If user explicitly selected a master menu (new or override), use it entirely.
  // Only fall back to existingMenu if user clicked "Continue with existing" or auto-skipped.

  const menuDataToUse = React.useMemo(() => {
    if (selectedMasterMenu) return selectedMasterMenu.menu_json;
    if (!existingMenu?.menu_json) return undefined;

    // For Fixed menus with an existing lead menu, merge fresh visibility from master
    const isFixed = existingMenu.is_fixed === true || existingMenu.is_fixed === 1;
    if (isFixed && existingMenu.master_menu_uuid) {
      const freshMasterMenu = masterMenusList.find(
        (m) => m.master_menu_uuid === existingMenu.master_menu_uuid
      );
      if (freshMasterMenu) {
        // Build a visibility map from the fresh master menu
        const masterVisibility: Record<string, boolean> = {};
        for (const cat of freshMasterMenu.menu_json) {
          if (cat.food_items) {
            for (const item of cat.food_items) {
              masterVisibility[item.food_item_uuid ?? ""] = (item as any).isVisible !== false;
            }
          }
          if (cat.subcategories) {
            for (const sub of cat.subcategories) {
              if (sub.food_items) {
                for (const item of sub.food_items) {
                  masterVisibility[item.food_item_uuid ?? ""] = (item as any).isVisible !== false;
                }
              }
            }
          }
        }

        // Apply fresh visibility to the existing lead menu data (preserving selections)
        return existingMenu.menu_json.map((cat) => ({
          ...cat,
          food_items: cat.food_items?.map((item) => {
            const uuid = item.food_item_uuid ?? "";
            const freshVisible = masterVisibility[uuid];
            return {
              ...item,
              isVisible: freshVisible !== undefined ? freshVisible : (item as any).isVisible,
            };
          }),
          subcategories: cat.subcategories?.map((sub) => ({
            ...sub,
            food_items: sub.food_items?.map((item) => {
              const uuid = item.food_item_uuid ?? "";
              const freshVisible = masterVisibility[uuid];
              return {
                ...item,
                isVisible: freshVisible !== undefined ? freshVisible : (item as any).isVisible,
              };
            }),
          })),
        }));
      }
    }

    return existingMenu.menu_json;
  }, [selectedMasterMenu, existingMenu, masterMenusList]);

  const limitsToUse = selectedMasterMenu
    ? extractLimits(selectedMasterMenu.menu_json)
    : existingMenu?.menu_json
      ? extractLimits(existingMenu.menu_json)
      : undefined;

  if (!open) return null;

  // ─── Render current step ──────────────────────────────────────────────────────

  const renderStep = () => {
    switch (step) {
      case "event":
        return (
          <EventSelectionStep
            events={events}
            guestName={selectedLeadData.name_of_guest}
            selectedLeadData={selectedLeadData}
            loading={leadFoodMenuLoading === LoadState.InProgress}
            onSelectEvent={handleSelectEvent}
          />
        );

      case "menu":
        return (
          <MasterMenuSelectionStep
            masterMenus={masterMenusList}
            loading={masterMenusLoading === LoadState.InProgress}
            existingMenuTitle={existingMenu?.title}
            currentMasterMenuUuid={existingMenu?.master_menu_uuid ?? selectedMasterMenu?.master_menu_uuid}
            hasExistingMenu={!!existingMenu}
            onSelectMasterMenu={handleSelectMasterMenu}
            onContinueExisting={handleContinueExisting}
            onBack={handleBackToEvents}
          />
        );

      case "builder":
        if (leadFoodMenuLoading === LoadState.InProgress && !existingMenu && !selectedMasterMenu) {
          return (
            <Box display="flex" alignItems="center" justifyContent="center" height="60vh">
              <CircularProgress size={40} />
            </Box>
          );
        }
        return (
          <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
            {/* Back toolbar */}
            <Box sx={{ display: "flex", alignItems: "center", px: 2, py: 0.5, borderBottom: "1px solid #E2E8F0" }}>
              <Button
                startIcon={<ArrowBack />}
                onClick={handleBackToMenus}
                size="small"
                sx={{ textTransform: "none", color: "#64748B" }}
              >
                Back to menu selection
              </Button>
            </Box>
            {/* FoodMenuBuilder */}
            <Box sx={{ flex: 1, overflow: "hidden" }}>
              <FoodMenuBuilder
                key={`${activeLeadUuid}-${selectedMasterMenu?.master_menu_uuid || "existing"}`}
                mode="lead"
                leadUuid={activeLeadUuid}
                masterMenuUuid={selectedMasterMenu?.master_menu_uuid ?? existingMenu?.master_menu_uuid ?? undefined}
                masterMenuName={selectedMasterMenu?.title ?? existingMenu?.master_menu_name ?? undefined}
                masterMenuPrice={selectedMasterMenu?.price != null ? String(selectedMasterMenu.price) : existingMenu?.master_menu_price ?? undefined}
                title={title}
                initialMenuData={menuDataToUse}
                initialLimits={limitsToUse}
                initialPrice={selectedMasterMenu?.price ?? (existingMenu?.master_menu_price ? Number(existingMenu.master_menu_price) : undefined)}
                initialIsFixed={selectedMasterMenu
                  ? selectedMasterMenu.is_fixed
                  : existingMenu?.is_fixed
                }
                onSave={handleSave}
                saving={saving}
                onPreview={(previewData) => {
                  setPreviewMenuData(previewData);
                  setOpenPreview(true);
                }}
              />
            </Box>
          </Box>
        );
    }
  };

  // ─── Mobile Layout ────────────────────────────────────────────────────────────

  if (isMobile) {
    return (
      <>
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: "background.paper",
            zIndex: 1300,
            display: "flex",
            flexDirection: "column",
            height: "100vh",
          }}
        >
          <Box
            sx={{
              bgcolor: "primary.main",
              color: "primary.contrastText",
              px: 2,
              py: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, flex: 1 }}>
              {title}
            </Typography>
            <Button
              onClick={handleClose}
              sx={{ minWidth: "auto", p: 0.5, color: "primary.contrastText" }}
            >
              <Close />
            </Button>
          </Box>
          <Box sx={{ flex: 1, overflow: "auto" }}>{renderStep()}</Box>
        </Box>

        {openPreview && (
          <LeadFoodMenuPreview
            open={true}
            uuid={leadUuid || ""}
            selectedLeadData={selectedLeadData}
            frontendPreviewData={previewMenuData}
            onClose={() => { setOpenPreview(false); setPreviewMenuData(null); }}
          />
        )}
      </>
    );
  }

  // ─── Desktop Layout ───────────────────────────────────────────────────────────

  return (
    <>
      <RightPanel
        heading={title}
        open={open}
        width="98%"
        onClose={handleClose}
        hideScroll
        sx={{padding: 0}}
        topRightButtons={
          <ShareViaWhatsAppButton
            type="button"
            request={whatsAppRequest}
            module="FOOD_MENU"
            size="small"
          />
        }
      >
        {renderStep()}
      </RightPanel>

      {openPreview && (
        <LeadFoodMenuPreview
          open={true}
          uuid={leadUuid || ""}
          selectedLeadData={selectedLeadData}
          frontendPreviewData={previewMenuData}
          onClose={() => { setOpenPreview(false); setPreviewMenuData(null); }}
        />
      )}
    </>
  );
};
